import React, { useState, useEffect } from 'react';
import { Row, Col, Typography, Divider, Button, Modal } from 'antd';
import { useParams, withRouter, RouteComponentProps } from 'react-router-dom';
import { isDocumentClassification, isMultiflow } from '../../../lib/util';
import { getAlgorithmFlowName } from '../../../lib/util/algorithm';
import { getFlow, getFlowStatistics } from '../../../lib/api/flow-api';
import { LabelGroup } from '../../../common/LabelGroup';
import InferenceList from './InferenceList';
import FlowDetailRules from './FlowDetailRules';
import FlowStatistic from './FlowStatistic';
import { Flow, FlowStatistic as FlowStatisticType } from '../../../types/flow';
import { PageHeader } from '../../../components/layout/PageHeader';
import { useGetAlgorithm } from '../../../lib/hooks';

const { Text } = Typography;

const getWhiteTitle = (title: string) => {
  return <Text style={{ color: 'white' }}>{title}</Text>;
};

interface Props extends RouteComponentProps {
  demo: boolean,
}

const FlowDetail = (props: Props) => {
  const { algorithmId, flowId } = useParams() as {
    algorithmId: string;
    flowId: string;
  };
  const [flow, setFlow] = useState<null | Flow>(null);
  const [filter, setFilter] = useState<null | string>(null);
  const [showFlowDetailRules, setShowFlowDetailRules] = useState<boolean>(false);
  const [statistics, setStatistics] = useState<null | FlowStatisticType>(null);

  const { algorithm } = useGetAlgorithm(algorithmId);

  useEffect(() => {
    const fetchFlow = async (flowId: string) => {
      const flow = await getFlow(flowId);
      setFlow(flow);
    };
    const fetchStatistics = async (flowId: string) => {
      const statistics = await getFlowStatistics(flowId);
      setStatistics(statistics);
    };

    fetchFlow(flowId);
    fetchStatistics(flowId);
  }, [flowId]);

  const updateStatistics = async () => {
    const statistics = await getFlowStatistics(flowId);
    setStatistics(statistics);
  };

  const showClasses = () => {
    Modal.info({
      title: 'Classes',
      content: <LabelGroup annotationTypes={(flow && flow.model && flow.model.info.annotationTypes) || []} />,
    });
  };

  return (
    <>
      {flow && showFlowDetailRules && (
        <FlowDetailRules
          onClose={() => {
            setShowFlowDetailRules(false);
          }}
          flow={flow}
        />
      )}
      {props.demo?
      <PageHeader
        header={flow && flow.name}
        description="Click on a file to check the flow results."
      />:
      <PageHeader
        back={{ url: `/flows/${algorithmId}`, label: (algorithm && getAlgorithmFlowName(algorithm.name)) || '' }}
        header={flow && flow.name}
        description="Click on a file to check the flow results."
      />}
      <Row>
        {!isMultiflow(algorithm) && (
          <>
            <Col span={4}>
              {algorithm && isDocumentClassification(algorithm) ? (
                <div>
                  <span style={{ marginRight: 16 }}>
                    <Text>Classes</Text>
                  </span>
                  <span>
                    <Text>
                      <Button
                        type="link"
                        className={'section-title'}
                        style={{ padding: 0 }}
                        onClick={() => {
                          showClasses();
                        }}>
                        {flow ? flow.model.info.annotationTypes.length : 0}
                      </Button>
                    </Text>
                  </span>
                </div>
              ) : (
                <div>
                  <span style={{ marginRight: 16 }}>
                    <Text>Labels and rules</Text>
                  </span>
                  <span>
                    <Text>
                      <Button
                        type="link"
                        className={'section-title'}
                        style={{ padding: 0 }}
                        onClick={() => {
                          setShowFlowDetailRules(true);
                        }}>
                        {`${flow && flow.model ? flow.model.info.annotationTypes.length : 0} labels (${
                          flow && flow.rules
                            ? flow.rules.fields.reduce((total, fieldRules) => total + fieldRules.rules.length, 0)
                            : 0
                        } rules)`}
                      </Button>
                    </Text>
                  </span>
                </div>
              )}
            </Col>
            <Col span={1}>
              <Divider type="vertical" className="h-full" />
            </Col>
          </>
        )}

        <Col className="flex" span={isMultiflow(algorithm) ? 24 : 19}>
          <Row gutter={14} className="container justify-between" /* className="mt-8 " */>
            <Col span={6}>
              <FlowStatistic
                title={algorithm && isDocumentClassification(algorithm) ? 'Total documents' : 'Total invoices'}
                value={statistics ? statistics.total : ''}
                color={'#7d7e82'}
                backgroundColor={'#e4e5e7'}
                allowHover={true}
                onClick={() => setFilter(null)}
              />
            </Col>
            <Col span={6}>
              <FlowStatistic
                title={getWhiteTitle(
                  algorithm && isDocumentClassification(algorithm) ? 'Successful documents' : 'Successful invoices'
                )}
                value={statistics ? statistics.success || 0 : ''}
                color={'#ffffff'}
                backgroundColor={'#52c41a'}
                allowHover={true}
                onClick={() => setFilter('SUCCESS')}
              />
            </Col>
            <Col span={6}>
              <FlowStatistic
                title={getWhiteTitle(
                  algorithm && isDocumentClassification(algorithm) ? 'Documents to check' : 'Invoices to check'
                )}
                value={statistics ? statistics.total - (statistics.success || 0) - statistics.progress : ''}
                color={'#ffffff'}
                backgroundColor={'#ff7029'}
                allowHover={true}
                onClick={() => setFilter('ERROR')}
              />
            </Col>
            <Col span={6}>
              <FlowStatistic
                title={getWhiteTitle('In progress')}
                value={statistics ? statistics.progress : ''}
                color={'#ffffff'}
                backgroundColor={'#fdaa00'}
                allowHover={true}
                onClick={() => setFilter('PROGRESS')}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="mt-10">
        <InferenceList
          statusChanged={updateStatistics}
          flow={flow}
          algorithm={algorithm}
          algorithmId={algorithmId}
          modelId={flow && flow.model ? flow.model.id : null}
          filter={filter}
          demo={props.demo}
        />
      </Row>
    </>
  );
};

export default withRouter(FlowDetail);
